// Browser-side output sink using the File System Access API.
// Same interface as src/output/local-fs.mjs so the exporter doesn't care
// which runtime it's in.
import { slugify, safeFilename, shortId } from '../util/slug.mjs';

export function createBrowserFsSink({ rootHandle }) {
  if (!rootHandle) throw new Error('rootHandle (FileSystemDirectoryHandle) required');
  const usedSlugs = new Map();

  async function getDir(parent, name) {
    return parent.getDirectoryHandle(name, { create: true });
  }
  async function chain(parent, ...parts) {
    let cur = parent;
    for (const p of parts) cur = await getDir(cur, p);
    return cur;
  }

  async function writeText(dir, name, content) {
    const fh = await dir.getFileHandle(name, { create: true });
    const w = await fh.createWritable();
    await w.write(content);
    await w.close();
  }

  async function init() {
    // ensure the data/ tree exists
    await chain(rootHandle, 'data');
  }

  function claimSlug(kind, name, id) {
    if (!usedSlugs.has(kind)) usedSlugs.set(kind, new Set());
    const used = usedSlugs.get(kind);
    const base = slugify(name);
    let slug = base;
    if (used.has(slug)) slug = `${base}-${shortId(id, 6)}`;
    let n = 2;
    while (used.has(slug)) slug = `${base}-${shortId(id, 6)}-${n++}`;
    used.add(slug);
    return slug;
  }

  async function openConversation({ kind, id, name, chat, teamSlug, existingRelDir = null }) {
    let parts, slug;
    if (existingRelDir) {
      // Incremental / resume mode: navigate to an existing conversation dir
      // instead of allocating a fresh slug. Path comes straight from the
      // saved manifest entry.
      parts = existingRelDir.replace(/^\/+|\/+$/g, '').split('/');
      slug = parts[parts.length - 1];
    } else {
      slug = claimSlug(`${kind}/${teamSlug || ''}`, name, id);
      parts = teamSlug
        ? ['data', kind, teamSlug, slug]
        : ['data', kind, slug];
    }
    const convDir = await chain(rootHandle, ...parts);
    const attachmentsDir = await getDir(convDir, 'attachments');
    const relDir = parts.join('/');

    const savedAttachments = new Map();

    async function writeAttachment({ id: attId, filename, stream, kind: attKind = 'file' }) {
      const safe = safeFilename(filename || `${attKind}-${shortId(attId)}`);
      const key = `${attId}::${safe}`;
      if (savedAttachments.has(key)) return savedAttachments.get(key);
      const finalName = uniquify(safe, savedAttachments);
      const fh = await attachmentsDir.getFileHandle(finalName, { create: true });
      const writable = await fh.createWritable();
      // stream is a Web ReadableStream from fetch's Response.body
      if (stream && typeof stream.pipeTo === 'function') {
        await stream.pipeTo(writable);
      } else if (stream) {
        // fallback for Node Readable — collect + write (only used in tests)
        const chunks = [];
        for await (const c of stream) chunks.push(c);
        await writable.write(new Blob(chunks));
        await writable.close();
      } else {
        await writable.close();
      }
      const rel = `attachments/${finalName}`;
      savedAttachments.set(key, rel);
      return rel;
    }

    async function writeConversation(payload) {
      await writeText(convDir, 'messages.json', JSON.stringify(payload, null, 2));
      const js = `window.__loadConversation(${JSON.stringify(payload.id)}, ${JSON.stringify(payload)});\n`;
      await writeText(convDir, 'conversation.js', js);
      return {
        messagesJson: `${relDir}/messages.json`,
        conversationJs: `${relDir}/conversation.js`,
      };
    }

    return { dir: convDir, relDir, slug, writeAttachment, writeConversation };
  }

  async function finalizeManifest(entries, opts = {}) {
    const dataDir = await getDir(rootHandle, 'data');
    // Pass generatedAt=null explicitly when writing a partial/rebuilt manifest;
    // the "Update backup" flow treats null as "no server-side chat filter" so
    // interrupted first-runs can be resumed cleanly.
    const generatedAt = 'generatedAt' in opts ? opts.generatedAt : new Date().toISOString();
    const payload = { generatedAt, conversations: entries };
    await writeText(dataDir, 'manifest.json', JSON.stringify(payload, null, 2));
    await writeText(dataDir, 'manifest.js', `window.__MANIFEST = ${JSON.stringify(payload)};\n`);
  }

  async function readMessagesJson(relDir) {
    const parts = relDir.replace(/^\/+|\/+$/g, '').split('/');
    let dir = rootHandle;
    for (const p of parts) dir = await dir.getDirectoryHandle(p);
    const fh = await dir.getFileHandle('messages.json');
    const file = await fh.getFile();
    return JSON.parse(await file.text());
  }

  async function readManifest() {
    try {
      const dataDir = await rootHandle.getDirectoryHandle('data');
      const fh = await dataDir.getFileHandle('manifest.json');
      const file = await fh.getFile();
      return JSON.parse(await file.text());
    } catch {
      return null;
    }
  }

  async function writeLog(content, { prefix = 'run' } = {}) {
    const logsDir = await getDir(rootHandle, 'logs');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    await writeText(logsDir, `${prefix}-${stamp}.log`, content);
    return `logs/${prefix}-${stamp}.log`;
  }

  return { root: rootHandle, init, openConversation, finalizeManifest, readMessagesJson, readManifest, writeLog };
}

function uniquify(name, existingMap) {
  const usedPaths = new Set([...existingMap.values()].map((p) => p.split('/').pop()));
  if (!usedPaths.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let n = 2;
  while (usedPaths.has(`${stem}-${n}${ext}`)) n++;
  return `${stem}-${n}${ext}`;
}
